/** @jsxImportSource @emotion/react */
import { Box, useTheme } from '@mui/material'
import { Accordion } from '@app/components/Atoms/Accordion'
import { Icon } from '@app/components/Atoms/Icon'
import { ScenariosByAsset } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/scenariosFetcher'
import { HazardTechnicalReport, HazardTechnicalReports } from '@app/components/Property/types'
import { shownHazardLayersAtom } from '@app/state/propertyAtoms'
import { useAtom } from 'jotai'
import { hazardColorDot } from '../HazardsTab.styles'
import { emptyHazardAccordion, toggleIcon } from './HazardAccordion.styles'
import { lazy } from 'react'

const UiMarkdown = lazy(() => import('@app/components/Atoms/UiMarkdown/UiMarkdown'))

interface HazardAccordionProps {
  isExposed: 'false' | 'true' | 'unknown'
  hazard: {
    display_name: string
    scenarios: string[]
    layer_ids?: string[]
    description?: string
    description_header?: string
  }
  scenariosByUid: Record<string, ScenariosByAsset>
  technicalReports: HazardTechnicalReports | null
  setPopupReport: (report: HazardTechnicalReport) => void
}

export const HazardAccordion = ({
  isExposed,
  scenariosByUid,
  technicalReports,
  setPopupReport,
  hazard,
}: HazardAccordionProps) => {
  const theme = useTheme()
  const [shownHazardLayers, setShownHazardLayers] = useAtom(shownHazardLayersAtom)

  const {
    description_header,
    display_name,
    scenarios: scenarioIds,
    description,
    layer_ids,
  } = hazard
  if (scenarioIds.length == 0) return null
  const hazardIds = Array.from(new Set(scenarioIds.map((s) => +s.split('_')[0].substring(1))))
  const exposureSentences = scenarioIds.flatMap((s) =>
    scenariosByUid[s]?.sentence ? [scenariosByUid[s]?.sentence] : [],
  )
  const exposureSentence = exposureSentences[0]
  const scenarioTechReports = hazardIds.flatMap((hazardId) => technicalReports?.[+hazardId] || [])
  // check if shownHazardLayers is scenarioIds
  const isShown =
    layer_ids !== undefined &&
    layer_ids.length > 0 &&
    shownHazardLayers.some((id) => layer_ids.includes(id))

  const hasExposureSentence = exposureSentence && exposureSentence.length > 0
  const hasTechnicalReports = scenarioTechReports && scenarioTechReports.length > 0
  const hasDescription = description && description.length > 0

  const header = (
    <Box css={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
      <Box
        css={hazardColorDot({
          is_exposed: isExposed,
        })}
      />
      <Box sx={{ flexGrow: '1' }}>{display_name}</Box>
      {layer_ids && layer_ids.length > 0 && (
        <Box
          css={toggleIcon(isShown)}
          onClick={(event) => {
            if (isShown) {
              setShownHazardLayers([])
            } else {
              setShownHazardLayers(layer_ids)
            }
            event.stopPropagation()
          }}
        >
          <Icon
            iconName={isShown ? 'EyeVisible' : 'EyeHidden'}
            colour={theme.palette.primary.main}
            size="medium"
          />
        </Box>
      )}
    </Box>
  )

  if (!hasExposureSentence && !hasTechnicalReports && !hasDescription) {
    return <Box css={emptyHazardAccordion(theme)}>{header}</Box>
  }
  return (
    <Accordion
      lazyContent={false}
      level="h3"
      variant="outline"
      defaultExpanded={false}
      header={<Box sx={{ paddingRight: '8px', width: '100%' }}>{header}</Box>}
      body={
        <Box
          css={{
            marginBottom: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {hasExposureSentence ? <Box>{exposureSentence}</Box> : null}
          {hasTechnicalReports ? (
            <>
              <h4
                css={{
                  marginTop: '8px',
                }}
              >
                Technical reports
              </h4>
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: '1px solid ' + theme.palette.white.dark,
                }}
              >
                {scenarioTechReports.map((report) => (
                  <Box
                    key={report.url}
                    css={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: '1px solid ' + theme.palette.white.dark,
                      padding: '4px 0',
                      gap: '16px',
                    }}
                  >
                    <Box
                      css={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {report.title}
                    </Box>
                    <a
                      onClick={() => setPopupReport(report)}
                      css={{
                        color: theme.palette.blue.main,
                        textDecoration: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      View
                    </a>
                  </Box>
                ))}
              </Box>
            </>
          ) : null}
          {hasDescription && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <h4
                css={{
                  marginTop: '8px',
                }}
              >
                {description_header ? description_header : `What is ${display_name}?`}
              </h4>
              <Box>
                <UiMarkdown>{description}</UiMarkdown>
              </Box>
            </Box>
          )}
        </Box>
      }
    />
  )
}
